import { readAgentView } from "../hub/api";
import { useEffect, useState } from "react";
import type { ApprovalCard } from "../lib/api";
import { connections, ordinaryForm, type ConnectionSettings } from "../connections/api";
import { useSettingsDirectory } from "../connections/context";
import { settingsTitle } from "../connections/directory";
import { firstClause, kindWords } from "./words";
import type { ToolIndex } from "./ask";

const object = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
export function configurationChanges(card: ApprovalCard, tools: ToolIndex): Record<string, Record<string, unknown>> | null {
  const declaration = object(tools[card.tool ?? ""]?.review?.configuration);
  const namespaces = typeof declaration.namespaces === "string" ? declaration.namespaces : "";
  if (namespaces) return Object.fromEntries(Object.entries(object(card.arguments[namespaces])).map(([key, value]) => [key, object(value)]));
  const namespace = typeof declaration.namespace === "string" ? card.arguments[declaration.namespace] : null;
  const values = typeof declaration.values === "string" ? card.arguments[declaration.values] : null;
  return typeof namespace === "string" ? { [namespace]: object(values) } : null;
}

/** The names a request takes back to their declared default -- a saved key
 * among them is cleared -- by namespace, where its tool declares them. */
export function configurationResets(card: ApprovalCard, tools: ToolIndex): Record<string, string[]> {
  const declaration = object(tools[card.tool ?? ""]?.review?.configuration);
  const namespace = typeof declaration.namespace === "string" ? card.arguments[declaration.namespace] : null;
  const names = typeof declaration.reset === "string" ? card.arguments[declaration.reset] : null;
  return typeof namespace === "string" && Array.isArray(names) && names.length ? { [namespace]: names.map(String) } : {};
}

/** Read-only review of the proposed values. Configuration and approval still
 * belong to their existing endpoints; no value is applied here. */
export function ConfigurationChanges({ changes, resets = {}, tools, agentId }: {
  changes: Record<string, Record<string, unknown>>; resets?: Record<string, string[]>; tools: ToolIndex; agentId?: string;
}) {
  const directory = useSettingsDirectory();
  const namespaces = Object.keys(changes);
  const declarations = namespaces.map(namespace => directory.find(entry => entry.namespace === namespace));
  // A namespace may survive reinstall while its fields no longer do. Hide old
  // values during this render, before the next declaration's fetch completes.
  const requestKey = JSON.stringify([agentId, namespaces.map((namespace, index) =>
    [namespace, declarations[index] ?? null])]);
  const [reading, setReading] = useState<{ scope: string; schemas: Record<string, ConnectionSettings>; unreadable: string[] } | null>(null);
  const schemas = reading?.scope === requestKey ? reading.schemas : {};
  const unreadable = reading?.scope === requestKey ? reading.unreadable : [];
  useEffect(() => {
    let live = true;
    Promise.all(namespaces.map(async (namespace, index) => {
      if (!declarations[index]) return [namespace, null] as const;
      try { return [namespace, await (agentId ? readAgentView<ConnectionSettings>(agentId, `/api/connections/${encodeURIComponent(namespace)}`) : connections.one(namespace))] as const; }
      catch { return [namespace, null] as const; }
    })).then(rows => {
      if (!live) return;
      setReading({ scope: requestKey,
        schemas: Object.fromEntries(rows.filter((row): row is readonly [string, ConnectionSettings] => row[1] !== null)),
        unreadable: rows.filter(row => row[1] === null).map(row => row[0]),
      });
    });
    return () => { live = false; };
  }, [requestKey, agentId]);
  return <div className="configuration-changes">
    <p>本次申请准备：{namespaces.map(namespace => settingsTitle(directory, namespace, "其他设置")).join("、")}。</p>
    {namespaces.map(namespace => {
      const values = changes[namespace];
      const schema = schemas[namespace]?.schema;
      const fields = schemas[namespace]?.fields;
      const ordinary = schema && fields ? ordinaryForm(fields, values).values : {};
      const unknown = schema ? Object.keys(values).filter(key => !schema.some(field => field.key === key)) : [];
      // What goes back to its default, named as its settings page names it;
      // a saved key among them is cleared, which is said apart.
      const secret = (key: string) => Boolean(fields?.find(item => item.key === key)?.secret);
      const names = (keys: string[]) => {
        const known = keys.map(key => fields?.find(item => item.key === key)?.label || "").filter(Boolean);
        return known.length ? `${known.join("、")}${known.length < keys.length ? `等 ${keys.length} 项` : ""}` : `${keys.length} 项`;
      };
      const cleared = resets[namespace] ?? [];
      const back = cleared.filter(key => !secret(key));
      const keys = cleared.filter(secret);
      return <section key={namespace} className="configuration-change-group">
        <h3>{settingsTitle(directory, namespace, schemas[namespace]?.title || "其他设置")}</h3>
        {back.length ? <p className="configuration-reset">改回默认：{names(back)}</p> : null}
        {keys.length ? <p className="configuration-reset">清除已存的密钥：{names(keys)}</p> : null}
        {Object.entries(object(directory.find(entry => entry.namespace === namespace)?.declared.review)).map(([field, raw]) => {
          const declaration = object(raw);
          const value = ordinary[field];
          if (value === undefined) return null;
          // A list of models is named on its settings page, from the registry
          // read there; this card has no such reading, so the values stay
          // under 查看具体设置值 instead of a row of 「未找到说明」.
          if (Array.isArray(value) && declaration.reference === "model") return null;
          const labels = object(declaration.value_labels);
          const titleFor = (key: unknown) => declaration.reference === "tool" ? tools[String(key)]?.act
            : declaration.reference === "category" ? String(object(declaration.key_labels)[String(key)] || "")
              : declaration.reference === "effect_class" ? kindWords(String(key)) : undefined;
          const named = (value: unknown) => typeof labels[String(value)] === "string" ? labels[String(value)] as string : "尚无说明，请查看具体设置值";
          // A list of declared values: each is called what its settings page calls it.
          const listed = (key: unknown) => titleFor(key)
            || (typeof labels[String(key)] === "string" ? labels[String(key)] as string : "");
          return <div key={field}>
            {typeof declaration.note === "string" ? <p>{declaration.note}</p> : null}
            {Array.isArray(value) ? <details><summary>{fields?.find(item => item.key === field)?.label || "本次设置清单"}（{value.length} 项）</summary><ul>{value.map((key, index) =>
              <li key={index}>{listed(key) || "当前未找到这项能力的说明，标识见明细"}</li>)}</ul></details>
              : value && typeof value === "object" ? Object.entries(object(value)).map(([key, mode]) => <p className="configuration-rule" key={key}>
                <span>{titleFor(key) || "尚无说明的操作（见明细）"}</span><strong>{named(mode)}</strong></p>)
                : <p>{fields?.find(item => item.key === field)?.label || "这项设置"}：{named(value)}</p>}
          </div>;
        })}
        <details>
          <summary>查看具体设置值</summary>
          {!schema ? <p>设置说明尚未读到，暂不展开参数值，以免显示凭据。</p> : null}
          {unknown.length ? <p>另有 {unknown.length} 项未声明的设置，暂不显示其值；请先与 IP 确认。</p> : null}
          {Object.entries(ordinary).map(([key, value]) => {
            const description = schema?.find(field => field.key === key)?.description;
            return <div key={key} className="configuration-value">
              <p>{description ? firstClause(description, 70) : "这项设置暂无说明"}</p>
              <pre data-quoted="true">{JSON.stringify({ [key]: value }, null, 2)}</pre>
            </div>;
          })}
          {schema?.some(field => field.secret && field.key in values) ? <p>密钥值不在这里显示。</p> : null}
        </details>
      </section>;
    })}
    {unreadable.length ? <p role="status">部分设置说明尚未读到，已保留本次请求明细。请先核对，不必急着批准。</p> : null}
  </div>;
}
